import { type Language } from "../lib/translations";
import { Modal } from "./Modal";
import { Calendar, ArrowUpRight } from "lucide-react";
import { type ProjectItem } from "./ProjectCard";

function parseJsonArray(jsonStr: string, fallback: any[] = []): any[] {
  try {
    return jsonStr ? JSON.parse(jsonStr) : fallback;
  } catch {
    return fallback;
  }
}

interface MetricsGridProps {
  metrics: any[];
  lang: Language;
}

function MetricsGrid({ metrics, lang }: MetricsGridProps) {
  return (
    <div className="space-y-2 border-t border-border/30 pt-4">
      <h5 className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">
        {lang === "tr" ? "Proje Metrikleri" : "Project Metrics"}
      </h5>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-2">
        {metrics.map((m: any, i: number) => (
          <div
            key={i}
            className="p-4 rounded-xl border border-border/40 bg-card/40 backdrop-blur-md text-center space-y-1.5 shadow-sm"
          >
            <div className={`font-poppins font-extrabold tracking-tight ${m.color || "text-brand-primary"} ${m.font_size || "text-xl"}`}>
              {lang === "tr" ? m.value_tr : m.value_en}
            </div>
            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
              {lang === "tr" ? m.label_tr : m.label_en}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface ProjectDetailsModalProps {
  project: ProjectItem;
  lang: Language;
  onClose: () => void;
}

export function ProjectDetailsModal({ project, lang, onClose }: ProjectDetailsModalProps) {
  const images = parseJsonArray(project.images);
  const tags = parseJsonArray(lang === "tr" ? project.tags_tr : project.tags_en);
  const metrics = parseJsonArray(project.metrics);
  const links = parseJsonArray(project.links);
  const techStack = (project.tech_stack || "").split(",").map((s) => s.trim()).filter(Boolean);

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={lang === "tr" ? project.title_tr : project.title_en}
      subtitle={`${project.version} — ${project.date}`}
    >
      <div className="space-y-6 text-left pb-4">
        {/* Project Tags */}
        <div className="flex flex-wrap gap-1.5">
          {tags.map((tag: string, i: number) => (
            <span
              key={i}
              className="text-[10px] uppercase font-bold text-brand-secondary bg-brand-secondary/5 px-3 py-1 rounded-full border border-border/40"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Images Showcase */}
        {images.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            {images.map((imgUrl: string, idx: number) => (
              <div key={idx} className="relative aspect-video rounded-xl overflow-hidden border border-border/40 shadow-sm bg-muted/20">
                <img
                  src={imgUrl}
                  srcSet={imgUrl.includes("/medium/") ? `${imgUrl.replace("/medium/", "/thumbs/")} 300w, ${imgUrl} 800w, ${imgUrl.replace("/medium/", "/large/")} 1600w` : undefined}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 800px"
                  alt="Project Screenshot"
                  loading="lazy"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        )}

        {/* Detailed Content */}
        <div className="text-sm leading-relaxed text-foreground/80 whitespace-pre-wrap font-sans">
          {lang === "tr" ? project.detailed_content_tr : project.detailed_content_en}
        </div>

        {/* Tech Stack List */}
        {techStack.length > 0 && (
          <div className="space-y-2 border-t border-border/30 pt-4">
            <h5 className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">Tech Stack</h5>
            <div className="flex flex-wrap gap-2">
              {techStack.map((tech, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 text-xs font-semibold rounded-md border border-border/40 bg-card/45 text-brand-primary"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Custom Metrics Grid */}
        {metrics.length > 0 && <MetricsGrid metrics={metrics} lang={lang} />}

        {/* Links section */}
        {links.length > 0 && (
          <div className="space-y-2 border-t border-border/30 pt-4">
            <h5 className="text-xs font-extrabold uppercase tracking-wider text-muted-foreground">Resources & Links</h5>
            <div className="flex flex-wrap gap-3">
              {links.map((link: any, idx: number) => (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-primary hover:text-brand-primary/80 hover:underline"
                >
                  <span>{lang === "tr" ? link.name_tr : link.name_en}</span>
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
